import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectStore } from '@/store/projectStore.ts';
import { useAiStore } from '@/store/aiStore.ts';
import { startTransitionVideoGen } from '@/store/actions.ts';
import { capabilities } from '@/capabilities.ts';
import type { ContinuousChainPair, TransitionAtTime } from '@/store/projectStore.ts';
import styles from './Viewer.module.css';

// ── Transition action buttons (regenerate/delete) ───────────────────
interface TransitionActionsProps {
  transInfo: TransitionAtTime | null;
  activePair: ContinuousChainPair | null;
  transVideoCountdown: number;
}

export function TransitionActions({ transInfo, activePair, transVideoCountdown }: TransitionActionsProps) {
  const { t } = useTranslation('viewer');
  const readOnly = useProjectStore(s => s.readOnly);
  const transitionVideoGenerating = useAiStore(s => s.transitionVideoGenerating);

  const handleRegenerate = useCallback(() => {
    const info = (transInfo && transInfo.transition.type === 'continuous')
      ? transInfo
      : activePair;
    if (!info) return;
    startTransitionVideoGen(info.fromShot.id, info.toShot.id);
  }, [transInfo, activePair]);

  const handleRemove = useCallback(() => {
    const info = transInfo ?? activePair;
    if (!info) return;
    useProjectStore.getState().removeTransitionVideo(info.fromShot.id, info.toShot.id);
  }, [transInfo, activePair]);

  if (!capabilities.aiGeneration || readOnly) return null;

  return (
    <div className={styles.transitionActions}>
      <button
        className={styles.transitionActionBtn}
        onClick={handleRegenerate}
        disabled={!!transitionVideoGenerating}
        title={t('regenerateTransitionTooltip')}
      >
        {!!transitionVideoGenerating ? <SpinnerIcon size={14} /> : '\u21BB'}
      </button>
      {!!transitionVideoGenerating && transVideoCountdown > 0 && (
        <span className={styles.transitionTimer}>{transVideoCountdown}s</span>
      )}
      <button
        className={styles.transitionActionBtn}
        onClick={handleRemove}
        title={t('removeTransitionTooltip')}
      >
        {'\u2715'}
      </button>
    </div>
  );
}

// ── Generate Transition button (centered, for continuous without video) ─
interface GenerateTransitionBtnProps {
  transInfo: TransitionAtTime | null;
  activePair: ContinuousChainPair | null;
  transVideoCountdown: number;
  onRequestGenerate?: () => void;
}

export function GenerateTransitionBtn({ transInfo, activePair, transVideoCountdown, onRequestGenerate }: GenerateTransitionBtnProps) {
  const { t } = useTranslation('viewer');
  const readOnly = useProjectStore(s => s.readOnly);
  const transitionVideoGenerating = useAiStore(s => s.transitionVideoGenerating);

  const handleGenerate = useCallback(() => {
    if (onRequestGenerate) {
      onRequestGenerate();
      return;
    }
    // Fallback: direct generation without confirm
    const info = (transInfo && transInfo.transition.type === 'continuous')
      ? transInfo
      : activePair;
    if (!info) return;
    startTransitionVideoGen(info.fromShot.id, info.toShot.id);
  }, [transInfo, activePair, onRequestGenerate]);

  if (!capabilities.aiGeneration || readOnly) return null;

  return (
    <button
      className={styles.generateTransBtn}
      onClick={handleGenerate}
      disabled={!!transitionVideoGenerating}
    >
      {!!transitionVideoGenerating ? (
        <>
          <SpinnerIcon size={16} />
          {transVideoCountdown > 0 ? ` ${transVideoCountdown}s` : ` ${t('generateTransitionGenerating')}`}
        </>
      ) : t('generateTransitionButton')}
    </button>
  );
}

// ── Shared spinner SVG ─────────────────────────────────────────────
export function SpinnerIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={styles.spinnerIcon}>
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2.5" strokeDasharray="50 20" />
    </svg>
  );
}
